import {
  Button,
  Divider,
  Dropdown,
  Input,
  Select,
  Switch,
  TimePicker,
  Tooltip
} from "antd";
import classNames from "classnames";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import ApiTaskManagement from "../../../../api/task-managements";
import useSearch from "../../user-management/useSearch";
import { Chips } from "../../../../components/chips/chips";
import { Icon } from "../../../../components/icon/icon";
import ApiPlanManagement from "../../../../api/plan-management";
import dayjs from "dayjs";
import TaskCard from "../../task-management/task-card/task-card";
import { SearchOutlined } from "@ant-design/icons";
import encodeSvgWithColor from "../../../../common/utils/encodeSvgWithColor";
import PageContentLoader from "../../../../components/loader/page-content-loader";
import VersionPage from "../../task-management/task-card/version-page";
import { useLocation } from "react-router-dom";
import { useProjectNavigate } from "../../../../common/utils/useProjectNavigate";
import WeekDayPickerModal from "./week-day-picker-modal";
import { CalendarOutlined, CloseOutlined, EllipsisOutlined } from "@ant-design/icons";
import ReminderList from "../../../../components/reminder-time/reminder-list";
import { ActivityControl, ActivityField } from "./activity-field";
import DurationStepper from "./duration-stepper";
import "./activity-modal.css";
import ApiProfileManagement from "../../../../api/profile-management";
import useAdminEvents from "../../../../hooks/useAdminEvents";
import { fitActivityDropdownToViewport } from "./activity-dropdown";
import {
  minutesToOffsetParts,
  offsetPartsToMinutes,
  rememberReminderTypeDraft,
  remindersFromActivity,
  remindersToPayload,
  switchReminderTypeDraft,
} from "./reminder-mapping";
const DAYS = ["su", "mo", "tu", "we", "th", "fr", "sa"];

const LOCK_TYPES = [
  { key: "never", label: "Never"},
  { key: "time", label: "On schedule"},
  { key: "timeout", label: "After timeout"},
  { key: "nextScenario", label: "When next arrives"}
]

const LOCK_PERIOD_OPTIONS = [
  { key: "endOfDay", label: "End of the day" },
  { key: "endOfWeek", label: "End of the week" },
];
const PLAN_ACTIVITY_BACKGROUND = "#6C6C8914";

const TaskDropdownComponent = ({
  children,
  task,
  planned,
  locked,
  lockType,
  day,
  activityId,
  replaceTaskToDay,
  replaceScenarioToLock,
  deleteTask,
  updateTaskNotificationTime,
  updateScenarioLockTime,
  tasksFullList,
  tasksList,
  categories,
  includeInDays,
  updateReschedule,
  rescheduling,
  updateDuration,
  duration,
  onOpen,
  onClose,
  readOnly = false,
  weeklySchedules,
  allPlanTasks,
  allPlanScenarios,
  currentActivity,
  onWeekDayPatch,
  lockPeriod,
  updateTaskLockPeriod,
  notificationType = "absolute",
  reminders = [],
  updateTaskReminders,
  resetTaskReminders,
  // LF360-1940: whether this activity has been changed at the plan level. False
  // means it follows the task, and the next publish of the task rewrites it.
  remindersCustomised = true,
  triggerSubjectProfileFieldId = null,
  triggerOffsetMinutes = 0,
  timeProfileFields = [],
  updateTaskNotificationType,
  updateTaskTriggerField,
  updateTaskTriggerOffsetMinutes,
  isScenario = false,
}) => {
  const { t } = useTranslation();
  const { avatar, avatarColor, title, backgroundColor, name } = task;
  const { remindHour, remindMinute } = planned;
  const { lockDays, lockHours, lockMinutes } = locked;
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState();
  const [showVersionPage, setShowVersionPage] = useState(false);
  const [relativeSign, setRelativeSign] = useState("+");
  const [relativeHours, setRelativeHours] = useState(0);
  const [relativeMinutes, setRelativeMinutes] = useState(0);
  const [isActivityMenuOpen, setIsActivityMenuOpen] = useState(false);
  const close = () => {
    setIsOpen(false);
    onClose();
  };
  const [isWeekDayModalOpen, setWeekDayModalOpen] = useState(false);

  useEffect(() => {
    const parts = minutesToOffsetParts(
      typeof triggerOffsetMinutes === "number" ? triggerOffsetMinutes : 0
    );
    setRelativeSign(parts.sign);
    setRelativeHours(parts.hours);
    setRelativeMinutes(parts.minutes);
  }, [triggerOffsetMinutes]);

  const handleSelectTask = () => {
    setIsActivityMenuOpen(false);

    const activeTaskWrapper = tasksFullList?.find((t) => t.DailyTask.id === task.id);
    const activeTask = activeTaskWrapper?.DailyTask;

    if (activeTask?.dailyTaskGroupId) {
      const editableVersionWrapper = tasksList.find(
        (t) =>
          t.DailyTask.dailyTaskGroupId === activeTask.dailyTaskGroupId &&
          (t.DailyTask.status === "pending" || t.DailyTask.status === "activeCopy")
      );
      const selected = editableVersionWrapper?.DailyTask || activeTask;
      setSelectedTask(selected);
      setIsOpen(true);
      onOpen();
      return;
    }

    if (task?.id) {
      const pathParts = location.pathname.split("/").filter(Boolean);
      const projectCode = pathParts[0];

      const scenarioUrl = `/${projectCode}/content/scenarios/${task.id}`;
      window.open(scenarioUrl, "_blank", "noopener,noreferrer");
    }
  };

  const disabledHours = () => {
    const hours = [];
    if (lockType === 'timeout') {
      return hours
    }
    for (let i = 0; i < remindHour; i++) {
      hours.push(i);
    }
    return hours;
  };

  const navigate = useProjectNavigate();
  const location = useLocation();

  const handleNavigate = (path) => {
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  const modalRef = useRef(null);
  const [weekPos, setWeekPos] = useState(null);

  const isTask = !!(task.title && avatar);
  const closePanel = () => setIsActivityMenuOpen(false);

  // Not memoised. The list it carried named eight values and missed the rest,
  // which was survivable only while the controls beneath it were uncontrolled and
  // read their value once: a defaultValue does not care whether its parent
  // re-renders. The design's duration, its switch and its selects are controlled,
  // so a stale memo would freeze them — an admin steps the duration up and the
  // number does not move. The triggered panel reached the same conclusion.
  const taskContentRender = (() => {
    return (
      <div ref={modalRef} className="activity-modal">
        {/* LF360-1881 — the task, a way through to it, and what can be done to
            the activity itself. Remove used to sit in a footer of its own; the
            design puts it behind the menu, which is where a destructive action
            belongs on a panel this small. */}
        <div className="activity-modal__header">
          <div className="activity-modal__identity">
            {avatar && (
              <div className="activity-modal__avatar" style={{ backgroundColor }}>
                <img src={encodeSvgWithColor(avatar, avatarColor)} alt="" />
              </div>
            )}
            <p className="activity-modal__title">{title || name}</p>
          </div>

          <Button
            className="activity-modal__open"
            icon={<Icon name={"arrow-right"} />}
            aria-label={isTask ? "Open task details" : "Open scenario"}
            onClick={handleSelectTask}
          />

          <span className="activity-modal__header-spacer" />

          <div className="activity-modal__actions">
            {!readOnly && (
              <Dropdown
                trigger={["click"]}
                getPopupContainer={() => modalRef.current || document.body}
                menu={{
                  items: [
                    {
                      key: "remove",
                      danger: true,
                      label: t(
                        isTask
                          ? "plan_management.task_modal.remove_task"
                          : "plan_management.task_modal.remove_scenario"
                      ),
                    },
                  ],
                  onClick: ({ key }) => {
                    if (key === "remove") deleteTask(activityId);
                  },
                }}
              >
                <Button
                  className="activity-modal__action"
                  aria-label="Activity actions"
                  icon={<EllipsisOutlined />}
                />
              </Dropdown>
            )}
            <Button
              className="activity-modal__action"
              aria-label="Close"
              icon={<CloseOutlined />}
              onClick={closePanel}
            />
          </div>
        </div>

        <div className="activity-modal__section">
          <ActivityField label={t("plan_management.task_modal.day")}>
            <ActivityControl className="activity-control--button">
              <Button
                className="activity-control__button"
                type="text"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  const r = modalRef.current?.getBoundingClientRect();
                  if (!r) return;

                  const width = 400;
                  const gap = 12;

                  let left = r.right + gap;
                  let top = r.top;

                  if (left + width > window.innerWidth - 8) {
                    left = Math.max(8, r.left - gap - width);
                  }

                  const maxTop = window.innerHeight - 8 - 200;
                  top = Math.max(8, Math.min(top, maxTop));

                  setWeekPos({ left, top, width });
                  setWeekDayModalOpen(true);
                }}
                disabled={readOnly}
              >
                <span>{t(`days.${day}`)}</span>
                <span className="activity-control__icon">
                  <CalendarOutlined />
                </span>
              </Button>
            </ActivityControl>
          </ActivityField>
          {/* LF360-1881: only a scenario still chooses between the two here — its
              type decides when the scenario itself fires. A task's reminders each
              say what they are, and it may hold both kinds at once, so a single
              answer for the whole task no longer means anything. */}
          {isScenario && (
            <ActivityField label="Trigger type">
              <ActivityControl>
                <Select
                  aria-label="Trigger type"
                  value={notificationType || "absolute"}
                  variant="borderless"
                  disabled={readOnly}
                  onChange={(value) => updateTaskNotificationType(value, activityId)}
                  options={[
                    { label: "Absolute", value: "absolute" },
                    { label: "Relative", value: "relative" },
                  ]}
                  getPopupContainer={() => modalRef.current || document.body}
                />
              </ActivityControl>
            </ActivityField>
          )}

          {isScenario && (notificationType || "absolute") === "absolute" && (
            <ActivityField label={t("plan_management.task_modal.time")}>
              <ActivityControl>
                <TimePicker
                  aria-label={t("plan_management.task_modal.time")}
                  disabled={readOnly}
                  format={"HH:mm"}
                  variant="borderless"
                  value={
                    Number.isInteger(remindHour) && Number.isInteger(remindMinute)
                      ? dayjs().hour(remindHour).minute(remindMinute)
                      : null
                  }
                  onChange={(value) => updateTaskNotificationTime(value, activityId)}
                  getPopupContainer={() => modalRef.current || document.body}
                />
              </ActivityControl>
            </ActivityField>
          )}

          {isScenario && (notificationType || "absolute") === "relative" && (
            <>
              <ActivityField label="Trigger">
                <ActivityControl>
                  <Select
                    aria-label="Trigger"
                    value={triggerSubjectProfileFieldId}
                    variant="borderless"
                    disabled={readOnly}
                    placeholder="Select time field"
                    onChange={(value) => updateTaskTriggerField(value, activityId)}
                    getPopupContainer={() => modalRef.current || document.body}
                  >
                    {timeProfileFields.map((field) => (
                      <Select.Option key={field.id} value={field.id}>
                        {field.title}
                      </Select.Option>
                    ))}
                  </Select>
                </ActivityControl>
              </ActivityField>

              <ActivityField label="Offset">
                <div className="activity-field__row">
                  <ActivityControl className="activity-control--narrow">
                    <Select
                      aria-label="Offset direction"
                      value={relativeSign}
                      variant="borderless"
                      disabled={readOnly}
                      onChange={(value) => {
                        setRelativeSign(value);
                        updateTaskTriggerOffsetMinutes(
                          offsetPartsToMinutes(value, relativeHours, relativeMinutes),
                          activityId
                        );
                      }}
                      options={[
                        { label: "+", value: "+" },
                        { label: "-", value: "-" },
                      ]}
                      getPopupContainer={() => modalRef.current || document.body}
                    />
                  </ActivityControl>

                  <ActivityControl>
                    <Input
                      aria-label="Offset hours"
                      value={relativeHours}
                      disabled={readOnly}
                      variant="borderless"
                      suffix="h"
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^\d]/g, "");
                        setRelativeHours(Math.min(23, Number(value || 0)));
                      }}
                      onBlur={() =>
                        updateTaskTriggerOffsetMinutes(
                          offsetPartsToMinutes(relativeSign, relativeHours, relativeMinutes),
                          activityId
                        )
                      }
                    />
                  </ActivityControl>

                  <ActivityControl>
                    <Input
                      aria-label="Offset minutes"
                      value={relativeMinutes}
                      disabled={readOnly}
                      variant="borderless"
                      suffix="m"
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^\d]/g, "");
                        setRelativeMinutes(Math.min(59, Number(value || 0)));
                      }}
                      onBlur={() =>
                        updateTaskTriggerOffsetMinutes(
                          offsetPartsToMinutes(relativeSign, relativeHours, relativeMinutes),
                          activityId
                        )
                      }
                    />
                  </ActivityControl>
                </div>
              </ActivityField>
            </>
          )}

          {/* A scenario's lock is its own question — when the scenario closes —
              and it has three shapes. A task's is the one below. */}
          {!avatar && (
            <ActivityField label={t("plan_management.task_modal.lock")}>
              <ActivityControl>
                <Select
                  aria-label={t("plan_management.task_modal.lock")}
                  options={LOCK_TYPES.map((l) => ({ label: l.label, value: l.key }))}
                  variant="borderless"
                  value={lockType || LOCK_TYPES[0].key}
                  onChange={(value) => replaceScenarioToLock(value, activityId)}
                  disabled={readOnly}
                  getPopupContainer={() => modalRef.current || document.body}
                />
              </ActivityControl>
            </ActivityField>
          )}

          {!avatar && "time" === lockType && (
            <ActivityField label={t("plan_management.task_modal.time")}>
              <ActivityControl>
                <TimePicker
                  aria-label="Lock time"
                  disabled={readOnly}
                  disabledHours={disabledHours}
                  format={"HH:mm"}
                  variant="borderless"
                  value={
                    Number.isInteger(lockHours) && Number.isInteger(lockMinutes)
                      ? dayjs().hour(lockHours).minute(lockMinutes)
                      : null
                  }
                  onChange={(value) =>
                    updateScenarioLockTime(value.$H, value.$m, null, activityId)
                  }
                  getPopupContainer={() => modalRef.current || document.body}
                />
              </ActivityControl>
            </ActivityField>
          )}

          {!avatar && "timeout" === lockType && (
            <ActivityField label={t("plan_management.task_modal.timeout")}>
              <div className="activity-field__row">
                <ActivityControl>
                  <Select
                    aria-label="Lock after days"
                    disabled={readOnly}
                    value={lockDays || 0}
                    onChange={(days) => {
                      updateScenarioLockTime(lockHours, lockMinutes, days, activityId);
                    }}
                    variant="borderless"
                    getPopupContainer={() => modalRef.current || document.body}
                  >
                    {[...Array(31).keys()].map((day) => (
                      <Select.Option key={day} value={day}>
                        {day}d
                      </Select.Option>
                    ))}
                  </Select>
                </ActivityControl>

                <ActivityControl>
                  <TimePicker
                    aria-label="Lock timeout time"
                    disabled={readOnly}
                    disabledHours={disabledHours}
                    format={"HH:mm"}
                    variant="borderless"
                    value={
                      Number.isInteger(lockHours) && Number.isInteger(lockMinutes)
                        ? dayjs().hour(lockHours).minute(lockMinutes)
                        : null
                    }
                    onChange={(value) => {
                      updateScenarioLockTime(value.$H, value.$m, lockDays, activityId);
                    }}
                    getPopupContainer={() => modalRef.current || document.body}
                  />
                </ActivityControl>
              </div>
            </ActivityField>
          )}

          {/* LF360-1934 named this one for what it does: it says when the task
              stops being completable, not when a scenario closes. */}
          {isTask && (
            <ActivityField label="Lock task">
              <ActivityControl>
                <Select
                  aria-label="Lock task"
                  value={lockPeriod}
                  variant="borderless"
                  disabled={readOnly}
                  onChange={(value) => updateTaskLockPeriod(value, activityId)}
                  options={LOCK_PERIOD_OPTIONS.map((item) => ({
                    label: item.label,
                    value: item.key,
                  }))}
                  getPopupContainer={() => modalRef.current || document.body}
                />
              </ActivityControl>
            </ActivityField>
          )}

          {isTask && (
            <ActivityField label={t("plan_management.task_modal.duration")}>
              <DurationStepper
                value={duration}
                onChange={(value) => updateDuration(value, activityId)}
                disabled={rescheduling || readOnly}
              />
            </ActivityField>
          )}

          {isTask && (
            <div className="activity-toggle">
              <Switch
                checked={rescheduling}
                onChange={(value) => updateReschedule(value, activityId)}
                size="small"
                disabled={duration > 1 || readOnly}
                id={`reschedule-${activityId}`}
              />
              <label
                className="activity-toggle__label"
                htmlFor={`reschedule-${activityId}`}
              >
                {t("plan_management.task_modal.reschedual")}
              </label>
            </div>
          )}
        </div>

        {/* Its own band, the way the design divides them: what the activity is
            above, when the subject hears about it below. */}
        {!isScenario && (
          <div className="activity-modal__section activity-modal__section--reminders">
            <ActivityField
              label={remindersCustomised ? "Reminder" : "Reminder (default)"}
            >
              <ReminderList
                value={reminders}
                onChange={(next) => updateTaskReminders?.(next, activityId)}
                readOnly={readOnly}
                triggerFields={timeProfileFields.map((field) => ({
                  value: field.id,
                  label: field.title,
                }))}
                popupContainerRef={modalRef}
              />
              {/* Only where there is something to go back to: an activity still
                  following the task has nothing to reset. */}
              {!readOnly && remindersCustomised && (
                <button
                  type="button"
                  className="activity-reset"
                  onClick={() => resetTaskReminders?.(activityId)}
                >
                  Reset to default
                </button>
              )}
            </ActivityField>
          </div>
        )}
      </div>
    );
  })();

    return (
      showVersionPage ? (
        <div
          className="version-page-overlay"
        >
          <VersionPage task={selectedTask} setShowVersionPage={setShowVersionPage}/>
        </div>
      ) : (
        <>
          <TaskCard
            isOpen={isOpen}
            close={close}
            task={selectedTask}
            categories={categories}
            onlyView={true}
            setShowVersionPage={setShowVersionPage}
            onOpenTask={() => handleNavigate(`/content/tasks/${selectedTask?.id}`)}
          />
          <Dropdown
            open={isActivityMenuOpen}
            onOpenChange={setIsActivityMenuOpen}
            popupRender={() => taskContentRender}
            trigger={["click"]}
            rootClassName="plan-activity-dropdown"
            onPopupAlign={fitActivityDropdownToViewport}
            // Rendered in the body, like the triggered-task panel that has always
            // behaved: pinning it inside .plan-page put it in a 100vh overflow:auto
            // container, so a panel that opened upwards landed at a negative offset
            // there — clipped, with no way to scroll to its header.
            //
            styles={{ root: { width: "max-content", zIndex: 100 } }}
          >
            {children}
          </Dropdown>
          <WeekDayPickerModal
            open={isWeekDayModalOpen}
            onCancel={() => setWeekDayModalOpen(false)}
            onApply={(patch) => {
              onWeekDayPatch?.(patch);
              setWeekDayModalOpen(false);
            }}
            weeklySchedules={weeklySchedules}
            allPlanTasks={isScenario ? (allPlanScenarios || []) : allPlanTasks}
            currentActivity={currentActivity}
            readOnly={readOnly}
            position={weekPos}
            isScenario={isScenario}
          />
        </>
      )
    );
  };

const AddItemComponent = ({
  items,
  onClick,
  handleAddTask,
  isScenario,
  variant,
}) => {
  const { search, handleUpdateSearchValue } = useSearch();

  const list = useMemo(() => {
    const availableTasks = search
      ? items.filter((item) =>
          (item.DailyTask ? item.DailyTask.title : item.name || item.title)
            ?.toLowerCase()
            .includes(search.toLowerCase())
        )
      : items;

    return availableTasks.map((item, index) => {
      const { avatar, avatarColor, backgroundColor, title, name, id } =
        item.DailyTask || item;

      return {
        key: id + index,
        label: title ?? name,
        icon: (avatar || backgroundColor) && (
          <div
            className="calendar-icon-wrapper"
            style={{
              backgroundColor,
            }}
          >
            {avatar && (
              <img
                style={{ width: 12 }}
                src={encodeSvgWithColor(avatar, avatarColor)}
                alt="avatar"
              />
            )}
          </div>
        ),
        onClick: () => handleAddTask(item),
      };
    });
  }, [items, search]);

  const dropdownRender = (content, isScenario) => {
    return (
      <div
        style={{
          padding: "0 10px",
          backgroundColor: "transparent",
        }}
      >
        <div
          className="ant-dropdown ant-dropdown-menu"
          style={{ boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px" }}
        >
          <div style={{ display: "flex", marginLeft: 15, padding: "3px" }}>
            <SearchOutlined />
            <Input
              variant="borderless"
              value={search}
              placeholder={'Search ' + (isScenario ? 'scenario' : 'task')}
              onChange={handleUpdateSearchValue}
            />
          </div>
          <Divider style={{ margin: "0 0 10px 0" }} />
          <div style={{ maxHeight: 260, overflow: "auto" }}>
            {React.cloneElement(content, { style: { boxShadow: "none" } })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dropdown
      menu={{
        items: list,
        onClick,
      }}
      trigger={["click"]}
      styles={{ root: {
        width: "max-content",
      } }}
      popupRender={(content) => dropdownRender(content, isScenario)}
    >
      <div>
        <Button
          type="text"
          backgroundColor="shade"
          fontSize={13}
          cursor="pointer"
          style={{
            color: "var(--text-1)",
            fontSize: 13,
            paddingLeft: 8,
          }}
          className={variant === "empty" ? "calendar-empty-add-btn" : "calendar-add-btn"}
        >
          {variant === "empty" ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1.1,
              }}
            >
              <Icon name="add" color="text-1" style={{ marginBottom: 4 }} />
              <span>{isScenario ? "Add scenario" : "Add task"}</span>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Icon name="add" color="text-1" style={{ marginRight: 4 }} />
              <span>{isScenario ? "Add scenario" : "Add task"}</span>
            </div>
          )}
        </Button>
      </div>
    </Dropdown>
  );
};

const CalendarTask = ({
  task,
  planned,
  locked,
  lockType,
  day,
  activityId,
  replaceTaskToDay,
  replaceScenarioToLock,
  deleteTask,
  updateTaskNotificationTime,
  updateScenarioLockTime,
  tasksList,
  tasksFullList,
  categories,
  includeInDays,
  updateReschedule,
  updateDuration,
  rescheduling,
  duration,
  id,
  isScenario,
  setActiveId,
  className,
  style,
  showTitleAndTime,
  showLockIcon,
  readOnly = false,
  notAllowed = false,
  weeklySchedules,
  allPlanTasks,
  allPlanScenarios,
  currentActivity,
  onWeekDayPatch,
  lockPeriod,
  updateTaskLockPeriod,
  notificationType = "absolute",
  reminders = [],
  updateTaskReminders,
  resetTaskReminders,
  remindersCustomised,
  triggerSubjectProfileFieldId = null,
  triggerOffsetMinutes = 0,
  timeProfileFields = [],
  updateTaskNotificationType,
  updateTaskTriggerField,
  updateTaskTriggerOffsetMinutes,
}) => {
  const { title, avatarColor, middleColor, name } = task || {};
  // An activity can outlive the scenario or task it points at (older data: the
  // delete path clears these now). It then arrives with no title and no name,
  // which used to throw in data-id and take the whole calendar down with it.
  // Render it as a broken item instead, so the admin can see it and remove it.
  const displayName = title || name;
  const { remindHour, remindMinute } = planned;

  const time =
    Number.isInteger(remindHour) && Number.isInteger(remindMinute)
      ? `${remindHour}:${remindMinute < 10 ? `0${remindMinute}` : remindMinute} `
      : "";

  const isActive = task?.isActive !== false;

  const taskColor = middleColor || avatarColor || "#000000";
  // const taskColor = "var(--text-1)";

  if (isScenario) {
    return (
      <TaskDropdownComponent
        isScenario={true}
        notificationType={notificationType}
        reminders={reminders}
        updateTaskReminders={updateTaskReminders}
        resetTaskReminders={resetTaskReminders}
        remindersCustomised={remindersCustomised}
        triggerSubjectProfileFieldId={triggerSubjectProfileFieldId}
        triggerOffsetMinutes={triggerOffsetMinutes}
        timeProfileFields={timeProfileFields}
        updateTaskNotificationType={updateTaskNotificationType}
        updateTaskTriggerField={updateTaskTriggerField}
        updateTaskTriggerOffsetMinutes={updateTaskTriggerOffsetMinutes}
        task={task}
        planned={planned}
        lockType={lockType}
        locked={locked}
        day={day}
        activityId={activityId}
        replaceTaskToDay={replaceTaskToDay}
        replaceScenarioToLock={replaceScenarioToLock}
        deleteTask={deleteTask}
        updateTaskNotificationTime={updateTaskNotificationTime}
        updateScenarioLockTime={updateScenarioLockTime}
        tasksFullList={tasksFullList}
        tasksList={tasksList}
        categories={categories}
        includeInDays={includeInDays}
        updateReschedule={updateReschedule}
        updateDuration={updateDuration}
        rescheduling={rescheduling}
        duration={duration}
        onOpen={() => setActiveId(id)}
        onClose={() => setActiveId(undefined)}
        style={{ width: "100%", height: 40 }}
        readOnly={readOnly}
        lockPeriod={lockPeriod}
        updateTaskLockPeriod={updateTaskLockPeriod}
        weeklySchedules={weeklySchedules}
        allPlanScenarios={allPlanScenarios}
        currentActivity={currentActivity}
        onWeekDayPatch={onWeekDayPatch}
      >
        <Tooltip
          open={notAllowed ? undefined : false}
          classNames={{ root: "plan-activity-not-allowed-tooltip" }}
          title={
            notAllowed
              ? "This task is no associated with the Plan’s cohort."
              : ""
          }
          placement="bottom"
          arrow={false}
        >
          <div
            className={`${className || "calendar-item"} ${
              notAllowed ? "calendar-item--not-allowed" : ""
            }`}
            data-id={id || (displayName || "unavailable").replace(/ /g, "-")}
            style={{
              marginBottom: 5,
              marginRight: 5,
              width: "100%",
              minWidth: "160px",
              opacity: isActive ? 1 : 0.3,
              position: "relative",
              borderRadius: 8,
              ...style,
            }}
          >
            <Chips
              text={
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    lineHeight: 1.2,
                  }}
                >
                  {showTitleAndTime && (
                    <>
                      <span
                        style={{
                          color: notAllowed ? "#151515" : "#151515",
                          fontWeight: 700,
                          marginRight: 4,
                        }}
                      >
                        {time}
                      </span>
                      <span
                        style={{
                          display: "inline-block",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "150px",
                          color: notAllowed ? "#151515" : "#151515",
                        }}
                      >
                        {displayName || "Unavailable content"}
                      </span>
                    </>
                  )}
                </div>
              }
              color={avatarColor ?? "#151515"}
              fontSize={13}
              backgroundColor={notAllowed ? "white" : PLAN_ACTIVITY_BACKGROUND}
              borderColor={PLAN_ACTIVITY_BACKGROUND}
              iconName={showLockIcon && !rescheduling && "locked"}
              cursor
              style={{
                width: "100%",
                height: 40,
                maxWidth: "200px",
                border: notAllowed ? "1px solid #FF3B30" : "none",
              }}
            />

            {notAllowed && !readOnly && (
              <Button
                type="text"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteTask?.(activityId);
                }}
                style={{
                  position: "absolute",
                  top: "calc(50% - 11px)",
                  right: 0,
                  padding: 0,
                  width: 22,
                  height: 22,
                  background: "transparent",
                  border: "none",
                  boxShadow: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: 2,
                  pointerEvents: "auto",
                }}
                icon={<Icon name="delete" color="#FF3B30" />}
              />
            )}
          </div>
        </Tooltip>
      </TaskDropdownComponent>
    );
  }

  return (
    <TaskDropdownComponent
      task={task}
      planned={planned}
      lockType={lockType}
      locked={locked}
      day={day}
      activityId={activityId}
      replaceTaskToDay={replaceTaskToDay}
      replaceScenarioToLock={replaceScenarioToLock}
      deleteTask={deleteTask}
      updateTaskNotificationTime={updateTaskNotificationTime}
      updateScenarioLockTime={updateScenarioLockTime}
      tasksFullList={tasksFullList}
      tasksList={tasksList}
      categories={categories}
      includeInDays={includeInDays}
      updateReschedule={updateReschedule}
      updateDuration={updateDuration}
      rescheduling={rescheduling}
      duration={duration}
      onOpen={() => setActiveId(id)}
      onClose={() => setActiveId(undefined)}
      style={{ width: "100%", height: 40 }}
      readOnly={readOnly}
      weeklySchedules={weeklySchedules}
      allPlanTasks={allPlanTasks}
      currentActivity={currentActivity}
      onWeekDayPatch={onWeekDayPatch}
      lockPeriod={lockPeriod}
      updateTaskLockPeriod={updateTaskLockPeriod}
      notificationType={notificationType}
      reminders={reminders}
      updateTaskReminders={updateTaskReminders}
      resetTaskReminders={resetTaskReminders}
      remindersCustomised={remindersCustomised}
      triggerSubjectProfileFieldId={triggerSubjectProfileFieldId}
      triggerOffsetMinutes={triggerOffsetMinutes}
      timeProfileFields={timeProfileFields}
      updateTaskNotificationType={updateTaskNotificationType}
      updateTaskTriggerField={updateTaskTriggerField}
      updateTaskTriggerOffsetMinutes={updateTaskTriggerOffsetMinutes}
    >
      <Tooltip
        open={notAllowed ? undefined : false}
        classNames={{ root: "plan-activity-not-allowed-tooltip" }}
        title={
          notAllowed
            ? "This task is no associated with the Plan’s cohort."
            : ""
        }
        placement="bottom"
        arrow={false}
      >
        <div
          className={`${className} ${
            notAllowed ? "calendar-item--not-allowed" : ""
          }`}
          data-id={id || (displayName || "unavailable").replace(/ /g, "-")}
          style={{
            marginBottom: 5,
            marginRight: 5,
            width: "100%",
            minWidth: "160px",
            opacity: isActive ? 1 : 0.3,
            position: "relative",
            borderRadius: 8,
            ...style,
          }}
        >
          {!isScenario && showTitleAndTime && (
            <div
              className="calendar-task-color-indicator"
              style={{
                backgroundColor: notAllowed ? "#FF3B30" : taskColor,
              }}
            />
          )}
          <Chips
            text={
              <div
                style={{
                  display: "flex",
                  alignItems: "stretch",
                  height: "100%",
                  lineHeight: 1.2,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    lineHeight: 1.2,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      lineHeight: 1.2,
                    }}
                  >
                    {showTitleAndTime && (
                      <>
                        <span
                          style={{
                            color: notAllowed ? "#151515" : "#151515",
                            fontWeight: 700,
                            marginRight: 4,
                          }}
                        >
                          {time}
                        </span>
                        <span
                          style={{
                            display: "inline-block",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "150px",
                            color: notAllowed ? "#151515" : "#151515",
                          }}
                        >
                          {displayName || "Unavailable content"}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            }
            color={notAllowed ? "#151515" : "#151515"}
            fontSize={13}
            backgroundColor={notAllowed ? "white" : PLAN_ACTIVITY_BACKGROUND}
            borderColor={notAllowed ? "#FF3B30" : PLAN_ACTIVITY_BACKGROUND}
            iconName={showLockIcon && !rescheduling && "locked"}
            cursor
            style={{
              width: "100%",
              height: 40,
              maxWidth: "200px",
              border: notAllowed ? "1px solid #FF3B30" : "none",
            }}
          />

          {notAllowed && !readOnly && (
            <Button
              type="text"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                deleteTask?.(activityId);
              }}
              style={{
                position: "absolute",
                top: "calc(50% - 11px)",
                right: 0,
                padding: 0,
                width: 22,
                height: 22,
                background: "transparent",
                border: "none",
                boxShadow: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                zIndex: 2,
                pointerEvents: "auto",
              }}
              icon={<Icon name="delete" color="#FF3B30" />}
            />
          )}
        </div>
      </Tooltip>
    </TaskDropdownComponent>
  );
};

const PlanCalendar = ({
  planId,
  scenariosList,
  planTasks,
  addTaskToPlan,
  updateTaskPlan,
  planScenarios,
  addScenarioToPlan,
  updateScenariosPlan,
  refreshPlan,
  weekIndex,
  weeklySchedules,
  readOnly = false,
  activeGroupScheduleIds = [],
  allPlanTasks,
  allPlanScenarios,
  onWeekDayPatch,
  planDefaultData,
}) => {
  const [timeProfileFields, setTimeProfileFields] = useState([]);
  const getTimeProfileFields = () => {
    ApiProfileManagement.getProfiles({ inputType: ["message:time"] }).then(({ data }) => {
      const { subjectProfileFields } = data || {};
      setTimeProfileFields(subjectProfileFields || []);
    });
  };
  const weekIndexRef = useRef(weekIndex);
  const reminderTypeDrafts = useRef(new Map());
  useEffect(() => {
    weekIndexRef.current = weekIndex;
  }, [weekIndex]);

  const { t } = useTranslation();

  const [hoverColumnIndex, setHoverColumnIndex] = useState(null);

  const [tasksList, setTasksList] = useState([]);
  const [tasksFullList, setTasksFullList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [id, setId] = useState(undefined);
  const [pauseHover, setPauseHoverd] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const allowedTaskIds = useMemo(
    () => (tasksList.length ? new Set((tasksList || []).map(t => t.DailyTask?.id)) : null),
    [tasksList]
  );

  const getTasks = (params) => {
    ApiTaskManagement.getTasks(params).then(({ data }) => {
      const { taskManagements, categories } = data || {};

      setTasksList((taskManagements || []).map((task) => ({ DailyTask: task })));
      setCategories(categories || []);
    });
  };

  const getTasksFullList = () => {
    ApiTaskManagement.getActiveTasks().then(({ data }) => {
      const { taskManagements } = data || {};

      setTasksFullList(taskManagements.map((task) => ({ DailyTask: task })));
    });
  };

  useAdminEvents({
    onCategoryChanged: () => {
      getTasks({ planIds: [planId], groupScheduleIds: activeGroupScheduleIds });
    },
    onTaskChanged: () => {
      getTasks({ planIds: [planId], groupScheduleIds: activeGroupScheduleIds });
      getTasksFullList();
    },
  });

  const highlightSameItems = (e) => {
    if (pauseHover) {
      return;
    }
    if (e.target.closest(".calendar-item")) {
      const _id = e.target.closest(".calendar-item").dataset.id;
      if (id !== _id) {
        setId(e.target.closest(".calendar-item").dataset.id);
      }
    } else if (id) {
      setId(undefined);
    }
  };

  useEffect(() => {
    getTasks({ planIds: [planId]});
    getTasksFullList();
    getTimeProfileFields();
  }, []);

  useEffect(() => {
    getTasks({ planIds: [planId], groupScheduleIds: activeGroupScheduleIds });
  }, [planId, JSON.stringify(activeGroupScheduleIds)]);

  useEffect(() => {
    document.addEventListener("mouseover", highlightSameItems);
    return () => document.removeEventListener("mouseover", highlightSameItems);
  }, [id]);

  const toggleOpen = (value) => {
    setId(value);
    if (value) {
      setPauseHoverd(true);
      return;
    }
    setPauseHoverd(false);
  };

  const viewAddBtns = (index: number | null) => setHoverColumnIndex(index);

  const addTask = (task, index) => {
    if (readOnly) return;
    setLoading(true);
    const addedTask = {
      ...task,
      day: DAYS[index],
      weekIndex: weekIndexRef.current,
      // LF360-1940: a task just added to the plan takes the reminders the task
      // carries. Nobody has made a plan-level choice about it yet, so there is
      // nothing here to protect from the next publish of the task. Said
      // explicitly because the backend reads silence as "the plan decided",
      // which is the right default everywhere except on the way in.
      remindersCustomised: false,
    };
    ApiPlanManagement.updateWeeklySchedule(planId, addedTask).then(() => {
      refreshPlan();
      setLoading(false);
    });
    addTaskToPlan(addedTask);
  };

  const addScenario = (scenario, index, dailyScenario = []) => {
    if (readOnly) return;
    const addedScenario = { ...scenario, day: DAYS[index], weekIndex: weekIndexRef.current };

    const lastOfDailyScenarioTime =
      dailyScenario[dailyScenario.length - 1]?.remindHour;

    let remindHour = scenario.remindHour;

    if (!remindHour && Number.isInteger(lastOfDailyScenarioTime)) {
      remindHour =
        lastOfDailyScenarioTime < 23 ? lastOfDailyScenarioTime + 1 : 23;
    }

    const newScenario = {
      scenarioId: scenario.id,
      day: DAYS[index],
      Scenario: scenario,

      notificationType: "absolute",
      triggerSubjectProfileFieldId: null,
      triggerOffsetMinutes: 0,

      remind: true,
      remindHour: remindHour ?? 7,
      remindMinute: scenario.remindMinute ?? 0,
      extraReminders: [],
      weekIndex: weekIndexRef.current,
    };
    ApiPlanManagement.updateWeeklySchedule(planId, newScenario).then(() => {
      refreshPlan();
    });

    addScenarioToPlan(addedScenario);
  };

  const deleteTask = (activityId) => {
    if (readOnly) return;
    setLoading(true);
    ApiPlanManagement.deleteActivity(planId, activityId).then(() => {
      refreshPlan();
      setLoading(false);
    });
  };

  const deleteScenario = (activityId) => {
    if (readOnly) return;
    ApiPlanManagement.deleteActivity(planId, activityId).then(() => {
      refreshPlan();
    });
  };

  const replaceTaskToDay = (newDay, activityId) => {
    if (readOnly) return;
    const changedTask = planTasks.find(
      (p) => (p.id || p.DailyTask.id) === activityId
    );
    setLoading(true);
    ApiPlanManagement.updateWeeklySchedule(planId, {
      ...changedTask,
      day: newDay,
      weekIndex: weekIndexRef.current
    }).then(() => {
      refreshPlan();
      setLoading(false);
    });

    const newPlan = planTasks.map((task) =>
      task.id === activityId ? { ...task, day: newDay } : task
    );
    updateTaskPlan(newPlan);
  };

  const replaceScenarioToLock = (newType, activityId) => {
    if (readOnly) return;
    const changedScenario = planScenarios.find((p) => p.id === activityId);
    ApiPlanManagement.updateWeeklySchedule(planId, {
      ...changedScenario,
      lockType: newType,
      lockHours: null,
      lockMinutes: null,
      weekIndex: weekIndexRef.current
    }).then(() => {
      refreshPlan();
    });

    const newPlan = planScenarios.map((scenario) =>
      scenario.id === activityId ? { ...scenario, lockType: newType, } : scenario
    );

    updateScenariosPlan(newPlan);
  };

  const replaceScenarioToDay = (newDay, activityId) => {
    if (readOnly) return;
    const changedScenario = planScenarios.find((p) => p.id === activityId);
    ApiPlanManagement.updateWeeklySchedule(planId, {
      ...changedScenario,
      day: newDay,
      weekIndex: weekIndexRef.current
    }).then(() => {
      refreshPlan();
    });

    const newPlan = planScenarios.map((scenario) =>
      scenario.id === activityId ? { ...scenario, day: newDay } : scenario
    );

    updateScenariosPlan(newPlan);
  };

  const updateDuration = (duration, activityId) => {
    if (readOnly) return;
    const changedTask = planTasks.find(
      (p) => (p.id || p.DailyTask.id) === activityId
    );
    setLoading(true);
    ApiPlanManagement.updateWeeklySchedule(planId, {
      ...changedTask,
      duration,
      weekIndex: weekIndexRef.current,
      day: changedTask.originalDay || changedTask.day,
    }).then(() => {
      refreshPlan();
      setLoading(false);
    });

    const newPlan = planTasks.map((task) =>
      task.id === activityId ? { ...task, duration } : task
    );
    updateTaskPlan(newPlan);
  };

  const updateTaskLockPeriod = (lockPeriod, activityId) => {
    if (readOnly) return;

    const changedTask = planTasks.find(
      (p) => (p.id || p.DailyTask.id) === activityId
    );
    if (!changedTask) return;

    ApiPlanManagement.updateWeeklySchedule(planId, {
      ...changedTask,
      lockPeriod,
      weekIndex: weekIndexRef.current,
      day: changedTask.originalDay || changedTask.day,
    }).then(() => {
      refreshPlan();
    });

    const newPlan = planTasks.map((task) =>
      task.id === activityId ? { ...task, lockPeriod } : task
    );
    updateTaskPlan(newPlan);
  };

  const updateReschedule = (rescheduling, activityId) => {
    if (readOnly) return;
    const changedTask = planTasks.find(
      (p) => (p.id || p.DailyTask.id) === activityId
    );

    ApiPlanManagement.updateWeeklySchedule(planId, {
      ...changedTask,
      rescheduling,
      weekIndex: weekIndexRef.current
    }).then(() => {
      refreshPlan();
    });

    const newPlan = planTasks.map((task) =>
      task.id === activityId ? { ...task, rescheduling } : task
    );
    updateTaskPlan(newPlan);
  };

  const updateScenarioReschedule = (rescheduling, activityId) => {
    if (readOnly) return;
    const changedScenario = planScenarios.find((p) => p.id === activityId);

    ApiPlanManagement.updateWeeklySchedule(planId, {
      ...changedScenario,
      rescheduling,
      weekIndex: weekIndexRef.current
    }).then(() => {
      refreshPlan();
    });

    const newPlan = planScenarios.map((scenario) =>
      scenario.id === activityId ? { ...scenario, rescheduling } : scenario
    );

    updateScenariosPlan(newPlan);
  };

  const updateScenarioLockTime = (hours, minutes, days, activityId) => {
    if (readOnly) return;
    const lockHours = hours + days*24;
    const lockMinutes = minutes;

    const changedScenario = planScenarios.find((p) => p.id === activityId);

    ApiPlanManagement.updateWeeklySchedule(planId, {
      ...changedScenario,
      lockHours,
      lockMinutes,
      weekIndex: weekIndexRef.current
    }).then(() => {
      refreshPlan();
    });

    const newPlan = planScenarios.map((scenario) =>
      scenario.id === activityId ? { ...scenario, lockHours, lockMinutes } : scenario
    );

    updateScenariosPlan(newPlan);
  }

  // LF360-1830: switching a task to Relative cannot be saved until a trigger field
  // is picked — the backend refuses it with TRIGGER_SUBJECT_PROFILE_FIELD_REQUIRED —
  // so that state lives only in local state. Any plan reload therefore wipes it, and
  // one always follows: the backend publishes plan_changed on every activity write
  // and the page reloads the plan on `weekly_activity_upserted`. That is why the type
  // flipped back to Absolute a second later.
  //
  // So the not-yet-saveable part of the edit is remembered and re-applied whenever
  // server data lands, until it becomes saveable and is persisted.
  const pendingLocalEditsRef = useRef(new Map());

  const rememberPendingEdit = (activityId, patch, type) => {
    const previous = pendingLocalEditsRef.current.get(activityId);
    pendingLocalEditsRef.current.set(activityId, {
      type: type || previous?.type,
      patch: { ...(previous?.patch || {}), ...patch },
    });
  };

  const forgetPendingEdit = (activityId) => {
    pendingLocalEditsRef.current.delete(activityId);
  };

  useEffect(() => {
    if (pendingLocalEditsRef.current.size === 0) {
      return;
    }

    const reapply = (collection, wantedType, apply) => {
      let changed = false;
      const next = collection.map((row) => {
        const pending = pendingLocalEditsRef.current.get(row.id);
        if (!pending || pending.type !== wantedType) {
          return row;
        }
        // Reference comparison is enough: the stored patch is not rebuilt, so once it
        // has been applied the values are identical and this settles.
        const differs = Object.keys(pending.patch).some((key) => row[key] !== pending.patch[key]);
        if (!differs) {
          return row;
        }
        changed = true;
        return { ...row, ...pending.patch };
      });

      if (changed) {
        apply(next);
      }
    };

    reapply(planTasks, 'task', updateTaskPlan);
    reapply(planScenarios, 'scenario', updateScenariosPlan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planTasks, planScenarios]);

  const findPlanItemByActivityId = (activityId) => {
    const task = planTasks.find((p) => (p.id || p.DailyTask?.id) === activityId);
    if (task) {
      return { item: task, type: "task" };
    }

    const scenario = planScenarios.find((p) => p.id === activityId);
    if (scenario) {
      return { item: scenario, type: "scenario" };
    }

    return { item: null, type: null };
  };

  const updatePlanItemLocally = (activityId, patch, type) => {
    if (type === "task") {
      updateTaskPlan(
        planTasks.map((task) =>
          task.id === activityId ? { ...task, ...patch } : task
        )
      );
      return;
    }

    if (type === "scenario") {
      updateScenariosPlan(
        planScenarios.map((scenario) =>
          scenario.id === activityId ? { ...scenario, ...patch } : scenario
        )
      );
    }
  };

  // `refresh: false` for a save whose result is already on screen. Refreshing pulls
  // the whole plan back from the server, and a refresh still in flight overwrites
  // local state that has deliberately not been saved yet — switching a task to
  // Relative, which the backend refuses until a trigger field is picked. That is why
  // the type appeared to flip back to Absolute a second later (LF360-1830).
  const savePlanItem = (item, payloadPatch, { refresh = true } = {}) => {
    const request = ApiPlanManagement.updateWeeklySchedule(planId, {
      ...item,
      ...payloadPatch,
      weekIndex: weekIndexRef.current,
      day: item.originalDay || item.day,
    });

    return refresh ? request.then(() => refreshPlan()) : request;
  };

  const updateTaskNotificationTime = (time, activityId) => {
    if (readOnly || !time) return;

    const remindHour = time.$H;
    const remindMinute = time.$m;

    const changedTask = planTasks.find(
      (p) => (p.id || p.DailyTask.id) === activityId
    );
    if (!changedTask) return;

    ApiPlanManagement.updateWeeklySchedule(planId, {
      ...changedTask,
      notificationType: "absolute",
      triggerSubjectProfileFieldId: null,
      triggerOffsetMinutes: 0,
      remind: true,
      remindHour,
      remindMinute,
      weekIndex: weekIndexRef.current,
      day: changedTask.originalDay || changedTask.day,
    }).then(() => {
      refreshPlan();
    });

    const newPlan = planTasks.map((task) =>
      task.id === activityId
        ? {
            ...task,
            notificationType: "absolute",
            triggerSubjectProfileFieldId: null,
            triggerOffsetMinutes: 0,
            remind: true,
            remindHour,
            remindMinute,
          }
        : task
    );
    updateTaskPlan(newPlan);
  };

  const updateTaskNotificationType = (notificationType, activityId) => {
    if (readOnly) return;

    const { item, type } = findPlanItemByActivityId(activityId);
    if (!item) return;

    // A scenario's time controls when it enters the chatbot. It is not the base
    // item in a multi-reminder list, so keep its original time/trigger contract
    // separate from the task reminder draft machinery.
    if (type === "scenario") {
      if (notificationType === "relative") {
        const patch = {
          notificationType: "relative",
          triggerSubjectProfileFieldId: item.triggerSubjectProfileFieldId || null,
          triggerOffsetMinutes:
            typeof item.triggerOffsetMinutes === "number"
              ? item.triggerOffsetMinutes
              : 0,
          remind: false,
          remindHour: null,
          remindMinute: null,
          extraReminders: [],
        };

        rememberPendingEdit(activityId, patch, type);
        updatePlanItemLocally(activityId, patch, type);
        return;
      }

      const patch = {
        notificationType: "absolute",
        triggerSubjectProfileFieldId: null,
        triggerOffsetMinutes: 0,
        remind: true,
        remindHour: Number.isInteger(item.remindHour) ? item.remindHour : 8,
        remindMinute: Number.isInteger(item.remindMinute) ? item.remindMinute : 0,
        extraReminders: [],
      };

      forgetPendingEdit(activityId);
      savePlanItem(item, patch);
      updatePlanItemLocally(activityId, patch, type);
      return;
    }

    const selectedDraft = switchReminderTypeDraft(
      reminderTypeDrafts.current,
      activityId,
      item.notificationType,
      remindersFromActivity(item),
      notificationType
    );
    const selected = remindersToPayload(selectedDraft, notificationType);

    if (notificationType === "relative") {
      const patch = {
        ...selected,
        triggerSubjectProfileFieldId: item.triggerSubjectProfileFieldId || null,
      };

      // Not saveable yet, so it has to survive the reload that the next write causes.
      rememberPendingEdit(activityId, patch, type);
      updatePlanItemLocally(activityId, patch, type);
      return;
    }

    const patch = { ...selected, triggerSubjectProfileFieldId: null };

    forgetPendingEdit(activityId);
    savePlanItem(item, patch);

    updatePlanItemLocally(activityId, patch, type);
  };

  // LF360-1830: a weekly task's reminders, as one list. The earliest one lands in
  // the activity's own columns (it is also what schedules the task) and the rest
  // in extraReminders — see reminder-mapping. Persisted through the same
  // savePlanItem path as every other reminder field, so the backend validates them
  // (a lead-time reminder is refused here — the task has no date before its day).
  const updateTaskReminders = (list, activityId) => {
    if (readOnly) return;

    const { item, type } = findPlanItemByActivityId(activityId);
    if (!item || type === "scenario") return;

    rememberReminderTypeDraft(
      reminderTypeDrafts.current,
      activityId,
      item.notificationType,
      list
    );
    // LF360-1940: editing the reminders here is the plan deciding for itself, so
    // this activity stops following the task and the next publish leaves it
    // alone.
    const patch = {
      ...remindersToPayload(list, item.notificationType || "absolute"),
      remindersCustomised: true,
    };

    // An offset with nothing to count from is refused by the backend, so it is
    // kept local until the row names a field; savePlanItem spreads the local
    // item, so it rides along with the next save. Read off what is about to be
    // sent rather than what is stored: the reminder carries the field now, and
    // the stored activity may still be an older shape that has none (LF360-1881).
    const awaitingTriggerField =
      patch.notificationType === "relative" && !patch.triggerSubjectProfileFieldId;

    if (awaitingTriggerField) {
      rememberPendingEdit(activityId, patch, type);
    } else {
      savePlanItem(item, patch, { refresh: false });
    }
    updatePlanItemLocally(activityId, patch, type);
  };

  // LF360-1940 — back to whatever the task says now.
  //
  // The same save as any other, with the marker set to false: the backend reads
  // that as "follow the task" and answers with the task's own set, so there is
  // no second route that could disagree with the first. Every task starts with
  // none, so today this clears the activity.
  const resetTaskReminders = (activityId) => {
    if (readOnly) return;

    const { item, type } = findPlanItemByActivityId(activityId);
    if (!item || type === "scenario") return;

    savePlanItem(item, { remindersCustomised: false }, { refresh: true });
  };

  const updateTaskTriggerField = (triggerSubjectProfileFieldId, activityId) => {
    if (readOnly) return;

    const { item, type } = findPlanItemByActivityId(activityId);
    if (!item) return;

    const patch = {
      notificationType: "relative",
      triggerSubjectProfileFieldId,
      triggerOffsetMinutes:
        typeof item.triggerOffsetMinutes === "number"
          ? item.triggerOffsetMinutes
          : 0,
      remind: false,
      remindHour: null,
      remindMinute: null,
      ...(type === "scenario" ? { extraReminders: [] } : {}),
    };

    // With a trigger field the relative state is saveable, so nothing is pending any
    // more — and savePlanItem spreads the local item, so the selected type's draft
    // is persisted with it.
    forgetPendingEdit(activityId);
    savePlanItem(item, patch);

    updatePlanItemLocally(activityId, patch, type);
  };

  const updateTaskTriggerOffsetMinutes = (triggerOffsetMinutes, activityId) => {
    if (readOnly) return;

    const { item, type } = findPlanItemByActivityId(activityId);
    if (!item) return;

    const patch = {
      notificationType: "relative",
      triggerOffsetMinutes,
      ...(type === "scenario" ? { extraReminders: [] } : {}),
    };

    updatePlanItemLocally(activityId, patch, type);

    if (!item.triggerSubjectProfileFieldId) {
      return;
    }

    forgetPendingEdit(activityId);
    savePlanItem(item, {
      ...patch,
      triggerSubjectProfileFieldId: item.triggerSubjectProfileFieldId,
      remind: false,
      remindHour: null,
      remindMinute: null,
    });
  };

  const updateScenarioNotificationTime = (time, activityId) => {
    if (readOnly || !time) return;

    const remindHour = time.$H;
    const remindMinute = time.$m;

    const changedScenario = planScenarios.find((p) => p.id === activityId);
    if (!changedScenario) return;

    const patch = {
      notificationType: "absolute",
      triggerSubjectProfileFieldId: null,
      triggerOffsetMinutes: 0,
      remind: true,
      remindHour,
      remindMinute,
      extraReminders: [],
    };

    ApiPlanManagement.updateWeeklySchedule(planId, {
      ...changedScenario,
      ...patch,
      weekIndex: weekIndexRef.current,
    }).then(() => {
      refreshPlan();
    });

    updateScenariosPlan(
      planScenarios.map((scenario) =>
        scenario.id === activityId ? { ...scenario, ...patch } : scenario
      )
    );
  };

  const getAbsoluteMinutes = (item) => {
    if (
      Number.isInteger(item?.remindHour) &&
      Number.isInteger(item?.remindMinute)
    ) {
      return item.remindHour * 60 + item.remindMinute;
    }

    return 7 * 60;
  };

  const getRelativeDefaultMinutes = (item) => {
    const fieldId = item?.triggerSubjectProfileFieldId;

    if (!fieldId) {
      return 7 * 60;
    }

    const rawDefault =
      planDefaultData?.notificationDefaults?.[fieldId];

    const defaultMinutes = Number(rawDefault);

    if (!Number.isFinite(defaultMinutes)) {
      return 7 * 60;
    }

    const offset =
      typeof item?.triggerOffsetMinutes === "number"
        ? item.triggerOffsetMinutes
        : 0;

    const result = defaultMinutes + offset;

    return ((result % 1440) + 1440) % 1440;
  };

  const getScenarioSortMinutes = (scenario) => {
    if (scenario?.notificationType === "relative") {
      return getRelativeDefaultMinutes(scenario);
    }

    return getAbsoluteMinutes(scenario);
  };

  const sortScenariosByTriggerTime = (a, b) => {
    return getScenarioSortMinutes(a) - getScenarioSortMinutes(b);
  };

  const calendarDaysRender = useMemo(() => {
    const tasksForHandleSelect = (index) => {
      const todayPlannedTasks =
        planTasks?.filter((pt) => pt.day === DAYS[index]) || [];

      const activeTasks = tasksList.filter(
        (t) => t.DailyTask.status === 'active'
      );

      if (todayPlannedTasks.length) {
        return activeTasks.filter(
          (t) =>
            !todayPlannedTasks.find(
              (task) => task.DailyTask.id === t.DailyTask.id
            )
        );
      }

      return activeTasks;
    };

    const scenariosForHandleSelect = (index) => {
      const todayPlannedScenarios =
        planScenarios?.filter((pt) => pt.day === DAYS[index]) || [];
      if (todayPlannedScenarios.length) {
        return scenariosList.filter(
          (t) => !todayPlannedScenarios.find((scenario) => scenario.id === t.id)
        );
      }
      return scenariosList;
    };

    const showTitleAndTime = (task, dayIndex, weekIndex, totalWeeks) => {
      const isSingleDay = task.duration === 1;
      if (isSingleDay) return true;

      const startWeek = task.originalWeek;
      const startDayIndex = DAYS.indexOf(task.originalDay);
      const totalDays = task.duration;

      let endWeek = startWeek;
      let remainingDays = totalDays - 1;
      let currentDayIndex = startDayIndex;

      while (remainingDays > 0) {
        currentDayIndex = (currentDayIndex + 1) % 7;
        if (currentDayIndex === 0) {
          endWeek = (endWeek + 1) % totalWeeks;
        }
        remainingDays--;
      }

      const isFirstDayShown = weekIndex === startWeek && dayIndex === startDayIndex;
      const isWeekStart = dayIndex === 0;

      const isRecycledTask = startWeek !== weekIndex;
      return isFirstDayShown || (isWeekStart && isRecycledTask);
    };

    const showLockIcon = (task, dayIndex, weekIndex, totalWeeks) => {
      const isSingleDay = task.duration === 1;
      if (isSingleDay) return true;

      const startWeek = task.originalWeek;
      const startDayIndex = DAYS.indexOf(task.originalDay);
      const totalDays = task.duration;

      let endWeek = startWeek;
      let remainingDays = totalDays - 1;
      let currentDayIndex = startDayIndex;

      while (remainingDays > 0) {
        currentDayIndex = (currentDayIndex + 1) % 7;
        if (currentDayIndex === 0) {
          endWeek = (endWeek + 1) % totalWeeks;
        }
        remainingDays--;
      }

      const isSaturday = dayIndex === 6;
      if (totalDays <= totalWeeks * 7) {
        const isLastDayShown = weekIndex === endWeek && dayIndex === currentDayIndex;
        return isLastDayShown || isSaturday;
      }

      const cycleLength = totalWeeks * 7;
      const lastBeforeStartDayIndex = (startDayIndex + cycleLength - 1) % 7;
      const lastBeforeStartWeek = (startWeek + Math.floor((startDayIndex + cycleLength - 1) / 7)) % totalWeeks;

      const isLastBeforeStart = weekIndex === lastBeforeStartWeek && dayIndex === lastBeforeStartDayIndex;
      return isLastBeforeStart || isSaturday;
    };

    const getTaskClass = (task, dayIndex, weekIndex, totalWeeks) => {
      const isSingleDay = task.duration === 1;
      if (isSingleDay) return "";

      const startWeek = task.originalWeek;
      const startDayIndex = DAYS.indexOf(task.originalDay);
      const totalDays = task.duration;

      let endWeek = startWeek;
      let remainingDays = totalDays - 1;
      let currentDayIndex = startDayIndex;

      while (remainingDays > 0) {
        currentDayIndex = (currentDayIndex + 1) % 7;
        if (currentDayIndex === 0) {
          endWeek = (endWeek + 1) % totalWeeks;
        }
        remainingDays--;
      }

      const isContinued = 
        dayIndex === 0 &&
        !(weekIndex === startWeek && startDayIndex === 0);

      const isFirstDayShown = weekIndex === startWeek && dayIndex === startDayIndex;
      const isLastDayShown =
        weekIndex === endWeek &&
        dayIndex === currentDayIndex &&
        totalDays <= totalWeeks * 7;

      if (isContinued && isLastDayShown) return "calendar-item--continued-end";
      if (isContinued) return "calendar-item--continued";
      if (isFirstDayShown) return "calendar-item--start";
      if (isLastDayShown) return "calendar-item--end";
      return "calendar-item--middle";
    };

    return (
      <div className={"plan-calendar"}>
        <div className="calendar-columns-container calendar-header">
          {DAYS.map((day, index) => (
            <p
              className={classNames("calendar-day", {
                "calendar-columns-items": index,
              })}
            >
              {t(`days.${day}`)}
            </p>
          ))}
        </div>

        {/* TASKS ROW */}
        <div className="calendar-columns-container calendar-tasks">
          {isLoading && (
            <div className="calendar-loader-overlay">
              <PageContentLoader backgroundOpacity={0.5} />
            </div>
          )}
          {DAYS.map((day, dayIndex) => {
            const tasksForDay = planTasks.filter((task) => task.day === DAYS[dayIndex]);
            const noTasksForDay = tasksForDay.length === 0;
            const maxPosition = tasksForDay.reduce((max, task) => Math.max(max, task.position), 0);
            let taskSlots = Array.from({ length: maxPosition  }, () => null);
            tasksForDay.forEach((task) => {
              taskSlots[task.position] = task;
            });

            const noTasks = planTasks.length === 0;
            const showTaskAddBtn = noTasks
              ? ( (hoverColumnIndex == null && dayIndex === 0) || hoverColumnIndex === dayIndex )
              : (hoverColumnIndex === dayIndex);

            return (
              <div
                className={dayIndex >= 0 ? "calendar-columns-items" : undefined}
                onMouseOver={() => viewAddBtns(dayIndex)}
                onMouseLeave={() => viewAddBtns(null)}
              >
                <div
                  key={day + "-calendar-column"}
                  className="calendar-column"
                >
                  {taskSlots.map((task, index) => 
                    task ? (() => {
                      const taskId = task?.DailyTask?.id ?? task?.id;
                      const isAllowed = allowedTaskIds ? allowedTaskIds.has(taskId) : true;

                      return (
                        <CalendarTask
                          readOnly={readOnly}
                          key={task.id}
                          id={task?.DailyTask?.id}
                          task={task.DailyTask ?? task}
                          className={`calendar-item ${getTaskClass(task, dayIndex, weekIndex, weeklySchedules.length)} ${!isAllowed ? 'calendar-item--not-allowed' : ''}`}
                          includeInDays={planTasks
                            ?.filter((pt) => pt.dailyTaskId === task.dailyTaskId)
                            .map((dt) => dt.day)}
                          rescheduling={task.rescheduling ?? true}
                          planned={{ remindHour: task.remindHour, remindMinute: task.remindMinute }}
                          locked={{}}
                          lockType={null}
                          day={task.day}
                          activityId={task.id}
                          replaceTaskToDay={replaceTaskToDay}
                          replaceScenarioToLock={replaceScenarioToLock}
                          deleteTask={deleteTask}
                          updateTaskNotificationTime={updateTaskNotificationTime}
                          tasksList={tasksList}
                          tasksFullList={tasksFullList}
                          categories={categories}
                          updateReschedule={updateReschedule}
                          updateDuration={updateDuration}
                          duration={task.duration}
                          setActiveId={toggleOpen}
                          span={task.duration}
                          style={{ "--task-color": task.DailyTask?.backgroundColor || "#ffcc00", width: "100%", height: 40 }}
                          showTitleAndTime={showTitleAndTime(task, dayIndex, weekIndex, weeklySchedules.length)}
                          showLockIcon={showLockIcon(task, dayIndex, weekIndex, weeklySchedules.length)}
                          notAllowed={!isAllowed}
                          weeklySchedules={weeklySchedules}
                          allPlanTasks={allPlanTasks}
                          currentActivity={task}
                          onWeekDayPatch={onWeekDayPatch}
                          lockPeriod={task.lockPeriod}
                          updateTaskLockPeriod={updateTaskLockPeriod}
                          notificationType={task.notificationType || "absolute"}
                          reminders={remindersFromActivity(task)}
                          updateTaskReminders={updateTaskReminders}
                          resetTaskReminders={resetTaskReminders}
                          remindersCustomised={task.remindersCustomised !== false}
                          triggerSubjectProfileFieldId={task.triggerSubjectProfileFieldId || null}
                          triggerOffsetMinutes={
                            typeof task.triggerOffsetMinutes === "number"
                              ? task.triggerOffsetMinutes
                              : 0
                          }
                          timeProfileFields={timeProfileFields}
                          updateTaskNotificationType={updateTaskNotificationType}
                          updateTaskTriggerField={updateTaskTriggerField}
                          updateTaskTriggerOffsetMinutes={updateTaskTriggerOffsetMinutes}
                        />
                  //   ) : (
                  //     <div key={`empty-${day}-${index}`} className="calendar-task-placeholder" />
                  //   )
                  // }))}
                      );
                    })() : (
                      <div key={`empty-${day}-${index}`} className="calendar-task-placeholder" />
                    )
                  )}

                  <div
                    className={classNames({
                      "calendar-add-btn--hidden": !showTaskAddBtn,
                    })}
                  >
                    {!readOnly && (
                      <AddItemComponent
                        items={tasksForHandleSelect(dayIndex)}
                        handleAddTask={(task) => addTask(task, dayIndex, weekIndexRef.current)}
                        variant={noTasks || noTasksForDay ? "empty" : undefined}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* SCENARIOS ROW */}
        <div className="calendar-columns-container calendar-scenarios">
          {DAYS.map((day, index) => {
            const noScenarios = planScenarios.length === 0;
            const showScenarioAddBtn = noScenarios
              ? ( (hoverColumnIndex == null && index === 0) || hoverColumnIndex === index )
              : (hoverColumnIndex === index);
            const scenariosForDay = planScenarios.filter((ps) => ps.day === day);
            const noScenariosForDay = scenariosForDay.length === 0;

            return (
              <div
                key={day + "calendar-scenario-column"}
                className={classNames("calendar-scenario-column", {
                  "calendar-columns-items": index,
                })}
                onMouseOver={() => viewAddBtns(index)}
                onMouseLeave={() => viewAddBtns(null)}
              >
                {planScenarios
                  ?.filter((planScenario) => planScenario.day === day)
                  .sort(sortScenariosByTriggerTime)
                  .map((scenario) => {
                    let { lockType, lockHours, lockMinutes, remindHour, remindMinute, day, id, rescheduling } =
                      scenario;
                    const planned = { remindHour, remindMinute };
                    let lockDays = 0
                    if (lockHours > 24) {
                      lockDays = Math.floor(lockHours / 24)
                      lockHours = lockHours % 24
                    }
                    const locked = { lockDays, lockHours, lockMinutes };
                    const includeInDays = planScenarios
                      ?.filter((pt) => pt.id === scenario.id)
                      .map((dt) => dt.day);
                    return (
                      <CalendarTask
                        readOnly={readOnly}
                        key={id}
                        id={scenario?.Scenario?.id}
                        task={scenario.Scenario ?? scenario}
                        isScenario={true}
                        includeInDays={includeInDays}
                        rescheduling={rescheduling ?? true}
                        planned={planned}
                        locked={locked}
                        lockType={lockType}
                        day={day}
                        activityId={id}
                        replaceTaskToDay={replaceScenarioToDay}
                        replaceScenarioToLock={replaceScenarioToLock}
                        deleteTask={deleteScenario}
                        updateTaskNotificationTime={
                          updateScenarioNotificationTime
                        }
                        updateScenarioLockTime={updateScenarioLockTime}
                        tasksList={tasksList}
                        categories={categories}
                        updateReschedule={updateScenarioReschedule}
                        setActiveId={toggleOpen}
                        showTitleAndTime={true}
                        showLockIcon={true}
                        weeklySchedules={weeklySchedules}
                        allPlanScenarios={allPlanScenarios}
                        currentActivity={scenario}
                        onWeekDayPatch={onWeekDayPatch}
                        notificationType={scenario.notificationType || "absolute"}
                        triggerSubjectProfileFieldId={scenario.triggerSubjectProfileFieldId || null}
                        triggerOffsetMinutes={
                          typeof scenario.triggerOffsetMinutes === "number"
                            ? scenario.triggerOffsetMinutes
                            : 0
                        }
                        timeProfileFields={timeProfileFields}
                        updateTaskNotificationType={updateTaskNotificationType}
                        updateTaskTriggerField={updateTaskTriggerField}
                        updateTaskTriggerOffsetMinutes={updateTaskTriggerOffsetMinutes}
                      />
                    );
                  })}
                <div
                  className={classNames({
                    "calendar-add-btn--hidden": !showScenarioAddBtn,
                  })}
                >
                  {!readOnly && (
                    <AddItemComponent
                      isScenario
                      items={scenariosForHandleSelect(index)}
                      handleAddTask={(scenario) =>
                        addScenario(
                          scenario,
                          index,
                          planScenarios
                            .filter((planScenario) => planScenario.day === day)
                            .sort(sortScenariosByTriggerTime)
                        )
                      }
                      variant={noScenarios || noScenariosForDay ? "empty" : undefined}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }, [
    planTasks,
    tasksList,
    planId,
    scenariosList,
    planScenarios,
    allPlanScenarios,
    hoverColumnIndex,
  ]);

  return (
    <div className="calendar">
      <style>
        {id
          ? `
              .calendar-item{
                opacity:0.4;
              }
              [data-id="${id}"]{
                opacity:1;
              }
            `
          : ""}
      </style>
      {calendarDaysRender}
    </div>
  );
};

export default PlanCalendar;
